<template>
  <div :class="users.length > 0 ? 'inner-container' : 'card'">
    <datatable
      :data="tableData"
      :updatingRow="updatingRowId"
      :loading="loading"
      :noResults="$t('datatable.empty_users')"
      :keepDataVisibleOnLoad="true"
      @onReachEnd="onReachEnd"
    />

    <side-panel v-if="articleScreenDataOpen" :onClose="onClosePanels">
      <load-content :loading="false">
        <form-user-permissions-articles
          v-if="editRow"
          :row="editRow"
          :onChange="(user_id, key, value) => updateRecord(user_id, key, value)"
        />
      </load-content>
    </side-panel>

    <side-panel v-if="viewCoderHomeOpen" :onClose="onClosePanels">
      <load-content :loading="false">
        <form-user-permissions-coder-home
          v-if="editRow"
          :row="editRow"
          :onChange="(user_id, key, value) => updateRecord(user_id, key, value)"
        />
      </load-content>
    </side-panel>
  </div>
</template>

<script>
import { FIELD_TYPE, USER_PERMISSIONS } from '../../config/const';
import i18n from '../../i18n';
import Datatable from '../datatable';
import SidePanel from '../side-panel';
import LoadContent from '../load-content';
import Button from '../button';
import FormUserPermissionsArticles from '../coding-admin/form-user-permissions-articles';
import FormUserPermissionsCoderHome from '../coding-admin/form-user-permissions-coder-home';

export default {
  props: {
    users: Array,
    updateRecord: Function,
    updatingRecord: Boolean,
    onSort: Function,
    pagination: Object,
    loading: Boolean
  },

  data: function() {
    return {
      articleScreenDataOpen: false,
      viewCoderHomeOpen: false,
      editRowId: null,
      updatingRowId: null
    }
  },

  computed: {
    editRow() { return this.users.find(user => user.user_id === this.editRowId) },
    tableData() {
      const { users } = this;
      const self = this;

      const editCodedArticlesOptions = [
        { value: 'assigned_only', label: this.$i18n.t(`user_permissions.edit_coded_articles_options.assigned_only`) },
        { value: 'yes', label: this.$i18n.t(`user_permissions.edit_coded_articles_options.yes`) },
        { value: 'no', label: this.$i18n.t(`user_permissions.edit_coded_articles_options.no`) }
      ];
      
      const bulkUpdateOptions = [
        { value: 'assigned_only', label: this.$i18n.t(`user_permissions.bulk_update_options.assigned_only`) },
        { value: 'yes', label: this.$i18n.t(`user_permissions.bulk_update_options.yes`) },
        { value: 'no', label: this.$i18n.t(`user_permissions.bulk_update_options.no`) }
      ];

      const assignArticlesOptions = [
        { value: 'self', label: this.$i18n.t(`user_permissions.assign_articles_options.self`) },
        { value: 'yes', label: this.$i18n.t(`user_permissions.assign_articles_options.yes`) },
        { value: 'no', label: this.$i18n.t(`user_permissions.assign_articles_options.no`) }
      ];

      const queryEngineOptions = [
        { value: 'limited', label: this.$i18n.t(`user_permissions.query_engine_options.limited`) },
        { value: 'yes', label: this.$i18n.t(`user_permissions.query_engine_options.yes`) },
        { value: 'no', label: this.$i18n.t(`user_permissions.query_engine_options.no`) }
      ];

      const bulkDeleteArticlesOptions = [
        { value: 'assigned_only', label: this.$i18n.t(`user_permissions.bulk_delete_articles_options.assigned_only`) },
        { value: 'yes', label: this.$i18n.t(`user_permissions.bulk_delete_articles_options.yes`) },
        { value: 'no', label: this.$i18n.t(`user_permissions.bulk_delete_articles_options.no`) }
      ];
      
      return {
        body: this.users,
        columns: [
          {
            key: 'email',
            label: this.$i18n.t(`user_permissions.email`),
            render: (row) => {
              const url = '/analysis/coders/' + row.coder_id;
              return (
                <a href={url} class="email-user">{row.email}</a>
              )
            }
          },
          {
            key: 'view_coding_ui',
            label: this.$i18n.t(`user_permissions.view_coding_ui`),
            input: {
              name: 'view_coding_ui',
              id: 'view_coding_ui',
              type: FIELD_TYPE.CHECKBOX,
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'article_screen_data',
            label: this.$i18n.t(`user_permissions.article_screen_data`),
            input: {
              component: {
                name: 'button',
                getProps: function(row) {
                  return {
                    label: '...',
                    row,
                    onClick: function(row) {
                      self.onOpenArticleScreenDataPanel(row);
                    },
                    buttonStyle: {
                      color: '#434343',
                      backgroundColor: 'transparent',
                      fontWeight: 700,
                      fontSize: '24px',
                      lineHeight: 1
                    }
                  }
                }
              }
            }
          },
          {
            key: 'edit_coded_articles',
            label: this.$i18n.t(`user_permissions.edit_coded_articles`),
            input: {
              name: 'edit_coded_articles',
              type: FIELD_TYPE.SELECT,
              options: editCodedArticlesOptions,
              allowEmpty: false,
              disableFixedPosition: true,
              placeholder: 'Select an option',
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'bulk_update',
            label: this.$i18n.t(`user_permissions.bulk_update`),
            input: {
              name: 'bulk_update',
              type: FIELD_TYPE.SELECT,
              options: bulkUpdateOptions,
              allowEmpty: false,
              disableFixedPosition: true,
              placeholder: 'Select an option',
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row);
              }.bind(this)
            },
          },
          {
            key: 'bulk_delete_articles',
            label: this.$i18n.t('user_permissions.bulk_delete_articles'),
            input: {
              name: 'bulk_delete_articles',
              type: FIELD_TYPE.SELECT,
              options: bulkDeleteArticlesOptions,
              allowEmpty: false,
              disableFixedPosition: true,
              placeholder: 'Select an option',
              onChange: this.onChangePermission
            },
          },
          {
            key: 'edit_project_setup',
            label: this.$i18n.t(`user_permissions.edit_project_setup`),
            input: {
              name: 'edit_project_setup',
              id: 'edit_project_setup',
              type: FIELD_TYPE.CHECKBOX,
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row);
              }.bind(this)
            },
          },
          {
            key: 'media_information_changes',
            label: this.$i18n.t(`user_permissions.media_information_changes`),
            input: {
              name: 'media_information_changes',
              id: 'media_information_changes',
              type: FIELD_TYPE.CHECKBOX,
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'view_coder_home',
            label: this.$i18n.t(`user_permissions.view_coder_home`),
            input: {
              component: {
                name: 'button',
                getProps: function(row) {
                  return {
                    label: '...',
                    row,
                    onClick: function(row) {
                      self.onOpenViewCoderHomePanel(row);
                    },
                    buttonStyle: {
                      color: '#434343',
                      backgroundColor: 'transparent',
                      fontWeight: 700,
                      fontSize: '24px',
                      lineHeight: 1
                    }
                  }
                }
              }
            }
          },
          {
            key: 'view_coder_auto_assign',
            label: this.$i18n.t(`user_permissions.view_coder_auto_assign`),
            input: {
              name: 'view_coder_auto_assign',
              id: 'view_coder_auto_assign',
              type: FIELD_TYPE.CHECKBOX,
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'view_coders_list',
            label: this.$i18n.t(`user_permissions.view_coders_list`),
            input: {
              name: 'view_coders_list',
              id: 'view_coders_list',
              type: FIELD_TYPE.CHECKBOX,
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'assign_articles',
            label: this.$i18n.t(`user_permissions.assign_articles`),
            input: {
              name: 'assign_articles',
              type: FIELD_TYPE.SELECT,
              options: assignArticlesOptions,
              allowEmpty: false,
              disableFixedPosition: true,
              placeholder: 'Select an option',
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'view_articles_group_notes',
            label: this.$i18n.t(`user_permissions.view_articles_group_notes`),
            input: {
              name: 'view_articles_group_notes',
              id: 'view_articles_group_notes',
              type: FIELD_TYPE.CHECKBOX,
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'export_articles_group_notes',
            label: this.$i18n.t(`user_permissions.export_articles_group_notes`),
            input: {
              name: 'export_articles_group_notes',
              id: 'export_articles_group_notes',
              type: FIELD_TYPE.CHECKBOX,
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'assign_articles_to_group',
            label: this.$i18n.t(`user_permissions.assign_articles_to_group`),
            input: {
              name: 'assign_articles_to_group',
              id: 'assign_articles_to_group',
              type: FIELD_TYPE.CHECKBOX,
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'view_qc',
            label: this.$i18n.t(`user_permissions.view_qc`),
            input: {
              name: 'view_qc',
              id: 'view_qc',
              type: FIELD_TYPE.CHECKBOX,
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'query_engine',
            label: this.$i18n.t(`user_permissions.query_engine`),
            input: {
              name: 'query_engine',
              type: FIELD_TYPE.SELECT,
              options: queryEngineOptions,
              allowEmpty: false,
              disableFixedPosition: true,
              placeholder: 'Select an option',
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'allow_refresh_mv',
            label: this.$i18n.t(`user_permissions.allow_refresh_mv`),
            input: {
              name: 'allow_refresh_mv',
              id: 'allow_refresh_mv',
              type: FIELD_TYPE.CHECKBOX,
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'allow_force_refresh_mv',
            label: this.$i18n.t(`user_permissions.allow_force_refresh_mv`),
            input: {
              name: 'allow_force_refresh_mv',
              id: 'allow_force_refresh_mv',
              type: FIELD_TYPE.CHECKBOX,
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          },
          {
            key: 'analysis_aricle_priority_queue',
            label: this.$i18n.t(`user_permissions.analysis_aricle_priority_queue`),
            input: {
              name: 'analysis_aricle_priority_queue',
              id: 'analysis_aricle_priority_queue',
              type: FIELD_TYPE.CHECKBOX,
              onChange: function(value, key, row) {
                this.onChangePermission(value, key, row)
              }.bind(this)
            },
          }
        ],
        fixedColumns: {
          enabled: true,
          fixedColumnsLeft: 1
        },
        custom_components: {
          'button': Button
        },
        order: {
          column: self.pagination.order,
          direction: self.pagination.sort,
          onChange: function(column, direction) {
            self.onSortChange(column, direction);
          }
        }
      }
    }
  },

  methods: {
    onClosePanels() {
      this.articleScreenDataOpen = false;
      this.viewCoderHomeOpen = false;
    },

    onOpenArticleScreenDataPanel(row) {
      this.editRowId = row.user_id;
      this.articleScreenDataOpen = true;
    },

    onOpenViewCoderHomePanel(row) {
      this.editRowId = row.user_id;
      this.viewCoderHomeOpen = true;
    },

    onSortChange(order, sort) {
      this.onSort(order, sort);
    },

    onChangePermission(value, key, row) {
      this.updateRecord(row.user_id, key, value);
      this.updatingRowId = row.user_permissions_id
    },

    onReachEnd() {
      this.$emit('loadMore');
    }
  },

  components: {
    Datatable,
    SidePanel,
    LoadContent,
    FormUserPermissionsArticles,
    FormUserPermissionsCoderHome
  },

  watch: {
    updatingRecord: {
      handler(value){ 
        if (!value) this.updatingRowId = null
      }
    }
  },
}
</script>

<style lang="scss" scoped>
  .link {
    color: $vis-primary-color;

    &:hover {
      opacity: 0.8;
    }
  }

  .email-user {
    color:#ec9148;

    &:hover {
      color: #27A8B1;
    }
  }
</style>